import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Keyboard, Zoom } from "swiper/modules";
import {
  ArrowLeft,
  Download,
  Maximize2,
  Minimize2,
  Brain,
  Bone,
  ShieldCheck,
  Droplet,
  Activity,
  Sparkles,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fetchProducts } from "@/lib/api";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/zoom";

const benefitIcons = [Brain, Bone, ShieldCheck, Droplet, Activity, Sparkles];

const ProductSlide = ({ product, fullscreen }) => {
  const accent = product.color_theme;
  return (
    <div className="h-full w-full bg-white">
      <div className="grid lg:grid-cols-2 h-full">
        {/* Image side */}
        <div
          className="relative flex items-center justify-center p-4 sm:p-8 lg:p-12 overflow-auto"
          style={{
            background: `linear-gradient(135deg, ${accent}10, ${accent}03)`,
          }}
        >
          <div className="swiper-zoom-container w-full h-full flex items-center justify-center">
            <img
              src={product.brochure}
              alt={`${product.name} brochure`}
              className="max-w-full max-h-full object-contain rounded-2xl shadow-lg"
              data-testid={`brochure-image-${product.slug}`}
            />
          </div>
          <Badge
            className="absolute top-4 left-4 rounded-full font-medium border-0 text-white shadow"
            style={{ backgroundColor: accent }}
          >
            {product.category}
          </Badge>
        </div>

        {/* Details side */}
        <div className="overflow-y-auto p-6 sm:p-10 lg:p-12 bg-white">
          <p
            className="text-xs font-bold uppercase tracking-widest"
            style={{ color: accent }}
          >
            Pediarange Series
          </p>
          <h2 className="mt-2 font-heading font-bold text-3xl sm:text-4xl tracking-tight text-foreground">
            {product.name}
          </h2>
          <p className="mt-2 text-base font-medium" style={{ color: accent }}>
            {product.tagline}
          </p>
          <p className="mt-4 text-sm sm:text-base text-muted-foreground leading-relaxed">
            {product.short_description}
          </p>

          <div className="mt-6">
            <h3 className="font-heading font-semibold text-foreground text-base mb-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4" style={{ color: accent }} />
              Composition
            </h3>
            <div className="rounded-2xl border border-border overflow-hidden">
              {product.composition.map((c, i) => (
                <div
                  key={c.name}
                  className={`flex items-center justify-between px-4 py-2.5 text-sm ${
                    i % 2 === 0 ? "bg-secondary/40" : "bg-white"
                  }`}
                >
                  <span className="text-foreground/85">{c.name}</span>
                  <span className="font-semibold text-foreground">{c.value}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <h3 className="font-heading font-semibold text-foreground text-base mb-3 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" style={{ color: accent }} />
              Key Benefits
            </h3>
            <ul className="grid sm:grid-cols-2 gap-2">
              {product.benefits.map((b, i) => {
                const Icon = benefitIcons[i % benefitIcons.length];
                return (
                  <li
                    key={b}
                    className="flex items-start gap-2 bg-secondary/40 p-3 rounded-xl text-sm"
                  >
                    <Icon
                      className="w-4 h-4 mt-0.5 shrink-0"
                      style={{ color: accent }}
                    />
                    <span className="text-foreground/85">{b}</span>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="mt-6">
            <h3 className="font-heading font-semibold text-foreground text-base mb-3 flex items-center gap-2">
              <Droplet className="w-4 h-4" style={{ color: accent }} />
              Packaging
            </h3>
            <div className="rounded-2xl bg-secondary/50 p-4 text-sm text-foreground/85 space-y-1">
              <p className="font-semibold text-foreground">{product.packaging}</p>
              {product.pack_details.map((d) => (
                <p key={d}>• {d}</p>
              ))}
              {product.flavour && (
                <p>
                  • <span className="font-semibold">Flavour:</span> {product.flavour}
                </p>
              )}
            </div>
          </div>

          <div className="mt-7 flex flex-wrap gap-3">
            <a
              href={product.brochure}
              target="_blank"
              rel="noreferrer"
              download={`${product.slug}-brochure.jpg`}
              data-testid={`download-brochure-${product.slug}`}
            >
              <Button
                size="lg"
                className="rounded-full h-12 px-6 text-white font-semibold"
                style={{ backgroundColor: accent }}
              >
                <Download className="mr-2 w-4 h-4" />
                Download Brochure
              </Button>
            </a>
            <a
              href={product.brochure}
              target="_blank"
              rel="noreferrer"
              data-testid={`open-brochure-${product.slug}`}
            >
              <Button
                size="lg"
                variant="outline"
                className="rounded-full h-12 px-6 border-2"
              >
                <Maximize2 className="mr-2 w-4 h-4" />
                Open full image
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

const CataloguePage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [searchParams] = useSearchParams();
  const containerRef = useRef(null);
  const swiperRef = useRef(null);

  const initialSlideSlug = searchParams.get("slide");

  useEffect(() => {
    fetchProducts()
      .then((p) => {
        setProducts(p);
        if (initialSlideSlug) {
          const idx = p.findIndex((x) => x.slug === initialSlideSlug);
          if (idx >= 0) setActiveIndex(idx);
        }
      })
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [initialSlideSlug]);

  const initialSlide = useMemo(() => {
    if (!initialSlideSlug || !products.length) return 0;
    const idx = products.findIndex((p) => p.slug === initialSlideSlug);
    return idx >= 0 ? idx : 0;
  }, [initialSlideSlug, products]);

  const toggleFullscreen = async () => {
    const el = containerRef.current;
    if (!el) return;
    try {
      if (!document.fullscreenElement) {
        await el.requestFullscreen?.();
        setFullscreen(true);
      } else {
        await document.exitFullscreen?.();
        setFullscreen(false);
      }
    } catch {
      setFullscreen((v) => !v);
    }
  };

  useEffect(() => {
    const onChange = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  return (
    <div
      ref={containerRef}
      data-testid="catalogue-page"
      className="min-h-screen bg-background flex flex-col"
    >
      {/* Catalogue Top Bar */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-border bg-white sticky top-0 z-30">
        <Link
          to="/"
          data-testid="back-to-home-btn"
          className="flex items-center gap-2 text-sm font-medium text-foreground/80 hover:text-primary"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Link>

        <div className="flex items-center gap-2">
          <p className="hidden sm:block text-sm font-heading font-semibold text-foreground">
            Pediarange · Digital Catalogue
          </p>
          {products.length > 0 && (
            <Badge variant="secondary" className="rounded-full text-xs">
              {activeIndex + 1} / {products.length}
            </Badge>
          )}
        </div>

        <button
          onClick={toggleFullscreen}
          data-testid="fullscreen-toggle-btn"
          className="w-10 h-10 grid place-items-center rounded-full bg-secondary text-primary hover:bg-primary hover:text-primary-foreground transition"
          aria-label="Toggle fullscreen"
        >
          {fullscreen ? (
            <Minimize2 className="w-4 h-4" />
          ) : (
            <Maximize2 className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Swiper Catalogue */}
      <div className="flex-1 relative">
        {loading ? (
          <div className="h-full grid place-items-center">
            <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <Swiper
              modules={[Navigation, Pagination, Keyboard, Zoom]}
              navigation={{
                prevEl: ".cat-prev",
                nextEl: ".cat-next",
              }}
              pagination={{ clickable: true }}
              keyboard={{ enabled: true }}
              zoom
              initialSlide={initialSlide}
              onSlideChange={(s) => setActiveIndex(s.activeIndex)}
              onSwiper={(s) => (swiperRef.current = s)}
              className="h-full"
              data-testid="catalogue-swiper"
            >
              {products.map((p) => (
                <SwiperSlide key={p.id}>
                  <ProductSlide product={p} fullscreen={fullscreen} />
                </SwiperSlide>
              ))}
            </Swiper>

            <button
              className="cat-prev absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-white text-primary shadow-lg grid place-items-center hover:bg-primary hover:text-primary-foreground transition"
              data-testid="catalogue-prev-btn"
              aria-label="Previous product"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              className="cat-next absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-white text-primary shadow-lg grid place-items-center hover:bg-primary hover:text-primary-foreground transition"
              data-testid="catalogue-next-btn"
              aria-label="Next product"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}
      </div>

      {/* Bottom thumbnail nav */}
      {products.length > 0 && (
        <div className="px-4 sm:px-6 py-3 border-t border-border bg-white">
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
            {products.map((p, i) => (
              <button
                key={p.id}
                onClick={() => swiperRef.current?.slideTo(i)}
                data-testid={`catalogue-thumb-${p.slug}`}
                className={`shrink-0 flex items-center gap-2 px-3 py-2 rounded-full border transition text-sm font-medium ${
                  activeIndex === i
                    ? "bg-primary text-primary-foreground border-primary shadow-md"
                    : "bg-white text-foreground/75 border-border hover:border-primary/40"
                }`}
              >
                <span className="w-6 h-6 rounded-full overflow-hidden bg-secondary shrink-0">
                  <img
                    src={p.image}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </span>
                {p.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CataloguePage;
